import { FieldInput } from "@/components/form/field-input";
import { FormContainer } from "@/components/form/form-container";
import { useForm } from "react-hook-form";
import { DownloadButton, SubmitButton } from "@/components/ui/button";
import {
  APDFilePayloadSchema,
  apdFilePayloadSchema,
  useAPDCreate,
} from "@/api/trackerdata-apd";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  swalError,
  swalErrorDefault,
  swalLoading,
  swalSuccess,
} from "@/utils/lib/swal";
import { useNavigate } from "react-router-dom";
import { useMediaDownload } from "@/api/media";
import { AxiosError } from "axios";
import { Anchor } from "@/components/ui/anchor";
import { useState } from "react";
import APDValidationTable from "./APDValidationTable";

export function APDUpload() {
  const form = useForm<APDFilePayloadSchema>({
    resolver: zodResolver(apdFilePayloadSchema),
  });
  const { mutate, isPending } = useAPDCreate();
  const { mutate: downloadMedia, isPending: isDownloading } =
    useMediaDownload();
  const [errorRows, setErrorRows] = useState([]);
  const [generalMessages, setGeneralMessages] = useState([]);

  const navigate = useNavigate();

  function handleDownload() {
    downloadMedia(
      {
        path: "/template-master-apd/Template-Master-APD.xlsx",
        fileName: "Template-Master-APD.xlsx",
      },
      {
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  }

  function handleSubmit(values: APDFilePayloadSchema) {
    swalLoading.fire({
      title: "Uploading APD",
      text: "Please wait, upload process is in progress",
    });
    mutate(
      { data: values },
      {
        onSuccess: () => {
          setErrorRows([]);
          setGeneralMessages([]);
          swalSuccess
            .fire({
              title: "Data Upload success",
              text: "You have successfully added data",
            })
            .then(() => navigate("/master-data/apd"));
        },
        onError: (error: Error) => {
          console.error(error);
          const err = error as AxiosError;
          //@ts-expect-error error type
          const dataWarn = err.response?.data?.data;
          const errorMessage = (err.response?.data as { data: unknown })?.data;

          if (err.response?.status === 417) {
            setErrorRows(dataWarn.warnMessages);
            setGeneralMessages(dataWarn.warnMessageAllRows);
            swalError.fire({
              title: "Data Upload failed",
              text: "Please check and try again",
            });
            return;
          }

          swalError.fire({
            title: "Data Upload failed",
            text: `${errorMessage}`,
          });
        },
      },
    );
  }

  return (
    <>
      <DownloadButton onClick={handleDownload} loading={isDownloading}>
        Download Template Master APD
      </DownloadButton>
      <FormContainer form={form} onSubmit={handleSubmit}>
        <FieldInput
          name="file"
          label="Upload APD Document (.xlxs)"
          type="file"
          accept=".xlsx, .xls"
        />
        <div className="ml-auto flex gap-x-2">
          <Anchor href="/master-data/apd" mood="axiata-blue-outline">
            Cancel
          </Anchor>
          <SubmitButton loading={isPending} />
        </div>
      </FormContainer>
      <div className="mt-10">
        <APDValidationTable
          errorRows={errorRows}
          generalMessages={generalMessages}
        />
      </div>
    </>
  );
}
